import { useEffect, useRef, useState } from 'react';
import { useAttachmentUrl } from './AttachmentPreview';
import { Icon } from './Icon';

/**
 * مشغّل تسجيل المكالمة.
 *
 * أكبر من عنصر `<audio>` الخام: تشغيلٌ وإيقاف، وشريطُ تقدّمٍ يُسحب،
 * وتقديمٌ وتأخيرٌ عشر ثوانٍ، وتحكّمٌ في السرعة — من يراجع مكالمةً يقفز
 * إلى موضع الاعتراض ويعيده، ويسرّع المقدّمات المكرّرة. واسمُ العميل
 * والتقييم فوقه: القصّة كاملةً في مكان الاستماع.
 *
 * الصوت يُجلب مرةً عند أول تشغيل — لا مع كل صفٍّ في الجدول.
 */

const SPEEDS = [1, 1.25, 1.5, 2] as const;

export function CallRecordingPlayer({
  id,
  title,
  meta,
  autoArm = false,
}: {
  id: string;
  title?: string | null;
  meta?: React.ReactNode;
  /** يُجلب الصوتُ فورًا — لمن فتح نافذةً غرضُها الاستماع وحده. */
  autoArm?: boolean;
}) {
  const [armed, setArmed] = useState(autoArm);
  const { url, loading, error } = useAttachmentUrl(id, armed);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);

  /*
   * مدّةُ التسجيل تُنتزَع انتزاعًا.
   *
   * ما سجّله المتصفّح يُكتب بثًّا حيًّا، فلا يعرف كاتبُه أين ينتهي قبل أن
   * ينتهي — فيصل الملفُّ ومدّتُه `Infinity`. وبلا مدّةٍ يموت شريطُ السحب
   * ويبقى الرقمُ صفرًا وإن دام التسجيلُ دقائق. والحيلةُ المعروفة: يُطلب
   * منه القفزُ إلى نهايةٍ بعيدة، فيقرأ الملفَّ إلى آخره ليعرف أين يقف،
   * فتنكشف المدّةُ الحقيقيّة — ثم يُعاد إلى أوّله كأن شيئًا لم يكن.
   */
  const measureDuration = (a: HTMLAudioElement) => {
    if (isFinite(a.duration) && a.duration > 0) {
      setDuration(a.duration);
      return;
    }
    const done = () => {
      a.removeEventListener("timeupdate", done);
      if (isFinite(a.duration)) setDuration(a.duration);
      a.currentTime = 0;
      setCurrent(0);
    };
    a.addEventListener("timeupdate", done);
    a.currentTime = 1e101;
  };

  /* السرعة تُطبَّق على العنصر كلما تغيّرت أو وصل مصدرٌ جديد. */
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed, url]);

  if (!armed) {
    return (
      <button type="button" className="btn btn-sm" onClick={() => setArmed(true)}>
        <Icon name="video" size={14} />
        استماع
      </button>
    );
  }

  if (error) {
    return (
      <span className="tiny" style={{ color: 'var(--danger)' }}>
        {error}
      </span>
    );
  }
  if (!url || loading) {
    return <span className="skeleton" style={{ width: 260, height: 60 }} />;
  }

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) void a.play();
    else a.pause();
  };

  const seek = (to: number) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min(to, duration || 0));
  };

  const nextSpeed = () => {
    const i = SPEEDS.indexOf(speed);
    setSpeed(SPEEDS[(i + 1) % SPEEDS.length]);
  };

  return (
    <div className="rec-player">
      {(title || meta) && (
        <div className="rec-player-head">
          {title && <strong className="rec-player-title">{title}</strong>}
          {meta}
        </div>
      )}

      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- تسجيل مكالمة لا مادة منشورة. */}
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={(e) => measureDuration(e.currentTarget)}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />

      <div className="rec-player-controls">
        <button
          className="rec-btn"
          onClick={() => seek(current - 10)}
          title="إرجاع ١٠ ثوانٍ"
          aria-label="إرجاع"
        >
          <Icon name="chevronRight" size={16} />
          <span className="tiny">١٠</span>
        </button>

        <button
          className="rec-btn rec-btn-play"
          onClick={toggle}
          aria-label={playing ? 'إيقاف' : 'تشغيل'}
        >
          <Icon name={playing ? 'clock' : 'chevronLeft'} size={18} />
        </button>

        <button
          className="rec-btn"
          onClick={() => seek(current + 10)}
          title="تقديم ١٠ ثوانٍ"
          aria-label="تقديم"
        >
          <span className="tiny">١٠</span>
          <Icon name="chevronLeft" size={16} />
        </button>

        <span className="rec-time num">{fmt(current)}</span>

        <input
          className="rec-seek"
          type="range"
          min={0}
          max={duration || 0}
          disabled={!duration}
          step={0.1}
          value={current}
          onChange={(e) => seek(Number(e.target.value))}
          aria-label="موضع التشغيل"
        />

        <span className="rec-time num">{fmt(duration)}</span>

        <button className="rec-speed num" onClick={nextSpeed} title="سرعة التشغيل">
          {speed}×
        </button>
      </div>
    </div>
  );
}

/** «م:ث» بخانتين للثواني. */
function fmt(sec: number): string {
  if (!isFinite(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
